import type { ButtonHTMLAttributes, MouseEventHandler } from 'react';
import { Button, ButtonVariants } from '../Button';
import { FormattedMessage } from '../Intl';
import { Tooltip } from '../Tooltip';
import { DialogFooterWrapper } from './styles';
import type { DialogProps } from './types';
import { ACTION_BUTTON_MINWIDTH, messages } from './utils';

export const DialogFooter = (
  props: Pick<
    DialogProps,
    | 'showCancel'
    | 'showConfirm'
    | 'cancelLabel'
    | 'confirmLabel'
    | 'variant'
    | 'confirmLoading'
    | 'confirmDisabled'
    | 'preventClosing'
    | 'onConfirmMouseOver'
    | 'stretchButtons'
    | 'confirmButtonTooltip'
    | 'showFooterBorder'
  > & {
    /** Defines the type of the confirm button.  Defaults to undefined, which means the confirm button will be a regular button.
     *
     * See {@link DialogProps} for how it is used in the Dialog component. */
    confirmButtonType?: HTMLButtonElement['type'];
    /** Cancel button click handler. */
    handleCancel?: MouseEventHandler<HTMLButtonElement>;
    /** Confirm button click handler. If confirmButtonType is 'submit', this will be ignored, in order to avoid double-submission. */
    handleConfirm?: MouseEventHandler<HTMLElement>;
    /** The form id to submit when the confirm button is clicked. */
    confirmButtonForm?: ButtonHTMLAttributes<HTMLButtonElement>['form'];
  }
) => {
  const {
    showCancel,
    showConfirm,
    cancelLabel,
    confirmLabel,
    variant = ButtonVariants.Primary,
    confirmLoading,
    confirmDisabled,
    preventClosing,
    onConfirmMouseOver,
    handleCancel,
    handleConfirm,
    stretchButtons,
    confirmButtonType,
    confirmButtonForm,
    showFooterBorder = true,
  } = props;
  return (
    <DialogFooterWrapper $showBorder={showFooterBorder}>
      {showCancel && (
        <Button
          data-testid="dialog-cancel"
          disabled={preventClosing}
          onClick={handleCancel}
          flex={stretchButtons ? '1' : undefined}
          style={{ minWidth: ACTION_BUTTON_MINWIDTH }}
        >
          {cancelLabel ?? <FormattedMessage {...messages.cancel} />}
        </Button>
      )}
      {showConfirm && (
        <Tooltip tooltip={props.confirmButtonTooltip}>
          <Button
            type={confirmButtonType}
            variant={variant}
            data-testid="dialog-confirm"
            disabled={confirmLoading || confirmDisabled || preventClosing}
            form={confirmButtonForm}
            onClick={confirmButtonType === 'submit' ? undefined : handleConfirm}
            loading={confirmLoading}
            onMouseOver={onConfirmMouseOver}
            flex={stretchButtons ? '1' : undefined}
            style={{ minWidth: ACTION_BUTTON_MINWIDTH }}
          >
            {confirmLabel ?? <FormattedMessage {...messages.confirm} />}
          </Button>
        </Tooltip>
      )}
    </DialogFooterWrapper>
  );
};
